/**
 * Auth service
 * @module services/auth
 */
import { fromJS, Map, List } from 'immutable'
import _map from 'lodash/map'
import _get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import localStorage from 'local-storage'
import request from '../request'

const DEVICE = 'admin-app'
const LOCAL_STORAGE_KEY = 'gaiaAdminAuth'

export const PERM_ACCESS_ADMIN_APP = 'access admin app'
export const PERM_ACCESS_CMS_V2 = 'access cms v2'
export const PERM_CREATE_CONTENT = 'create content'
export const PERM_DELETE_CONTENT = 'delete content'
export const PERM_UPDATE_CONTENT = 'update content'
export const PERM_VIEW_CONTENT = 'view content'
export const PERM_VIEW_MEDIA = 'view media'
export const PERM_BULK_UPDATE_CONTENT = 'bulk update content'

/**
 * Get user permissions from the server
 * @param {Object} options The options
 * @param {import('immutable').Map} options.auth The auth object
 * @param {String} options.server The server
 * @returns {Promise<import('immutable').List>} List of permission names
 */
export async function getUserPermissions (options = {}) {
  const { auth, server } = options
  const res = await request
    .get(`${server}/v1/permissions`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(_map(_get(res, ['body', 'permissions'], []), 'name'))
}

/**
 * Login a user
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {String} options.username The username
 * @param {String} options.password The password
 * @returns {Promise<Map>} A map that is the response body
 */
export async function login (options = {}) {
  const { server, username, password } = options
  const { body: data = {} } = await request
    .post(`${server}/v1/login`)
    .send({ username, password, device: DEVICE })
    .type('application/x-www-form-urlencoded')
    .set('accept', 'application/json')
  const permissions = await getUserPermissions({
    auth: fromJS({ data }),
    server,
  })
  return fromJS({ ...data, permissions })
}

/**
 * Renew authentication token
 * @param {Object} options The options
 * @param {Map} options.auth The auth state
 * @param {String} options.server The server url
 * @returns {Promise<Map>} A map that is the response
 */
export async function renew (options = {}) {
  const { server, auth } = options
  const { body: data = {} } = await request
    .post(`${server}/v1/renew`)
    .send({ device: DEVICE })
    .type('application/x-www-form-urlencoded')
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  const permissions = await getUserPermissions({
    auth: fromJS({ data }),
    server,
  })
  return fromJS({ ...data, permissions })
}

/**
 * Logout a user
 * @param {Object} options The options
 * @param {Map} options.auth The auth state
 * @param {String} options.server The server url
 * @returns {Promise<Map>} A map that is the response body
 */
export async function logout (options = {}) {
  const { server, auth } = options
  const res = await request
    .get(`${server}/v1/logout`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(_get(res, 'body', {}))
}

/**
 * Set auth data in local storage
 * @param {Map} data The data to set in local storage
 */
export function setAuthLocal (data) {
  if (!data) {
    localStorage.remove(LOCAL_STORAGE_KEY)
    return
  }
  const localData = data.toJS()
  localStorage(LOCAL_STORAGE_KEY, localData)
}

/**
 * Get auth data from local storage
 * @returns {Map|null} The auth data in local storage
 * or null if it does not exist
 */
export function getAuthLocal () {
  const localData = localStorage(LOCAL_STORAGE_KEY)
  if (localData) {
    return fromJS(localData)
  }
  return null
}

/**
 * Given an auth state return the jwt value
 * @param {Map|null} auth The auth state or a nill value
 * @returns {Boolean} True when the user is logged in
 */
export function getAuthJwt (auth) {
  return Map.isMap(auth) ? auth.getIn(['data', 'jwt']) : undefined
}

/**
 * Given an auth state return the uid value
 * @param {Map|null} auth The auth state or a nill value
 * @returns {String|undefined} The user id
 */
export function getAuthUid (auth) {
  return Map.isMap(auth) ? auth.getIn(['data', 'uid']) : undefined
}

/**
 * Given an auth state return the username value
 * @param {Map|null} auth The auth state or a nill value
 * @returns {String|undefined} The username
 */
export function getAuthUsername (auth) {
  return Map.isMap(auth) ? auth.getIn(['data', 'username']) : undefined
}

/**
 * Given an auth state return the roles
 * @param {Map|null} auth The auth state or a nill value
 * @returns {List<Number>|undefined} A list of role ids
 */
export function getAuthRoles (auth) {
  return Map.isMap(auth) ? auth.getIn(['data', 'roleIds'], List()) : undefined
}

/**
 * Given an auth state return the permissions
 * @param {Map|null} auth The auth state or a nill value
 * @returns {List<String>} A list of permissions names
 */
export function getAuthPermissions (auth) {
  return Map.isMap(auth) ? auth.getIn(['data', 'permissions'], List()) : List()
}

/**
 * Given an auth state return true if it indicates the user
 * is logged in i.e. the data exists and idExpires is in the future
 * @param {Map|null} auth The auth state or a nil value
 * @param {Number} [now] Now as a unix timestamp, default to current timestamp
 * @returns {Boolean} True when the user is logged in
 */
export function isLoggedIn (auth, now = Math.floor(Date.now() / 1000)) {
  if (!Map.isMap(auth)) {
    return false
  }
  const idExpires = auth.getIn(['data', 'idExpires']) || 0
  return !isEmpty(getAuthJwt(auth)) && idExpires > now
}

/**
 * Create authorization value given the auth state
 * @param {Map|null} auth The auth state or a nill value
 * @returns {String|undefined} The JWT values prefixed with Bearer or undefined
 */
export function createAuthorization (auth) {
  const jwt = getAuthJwt(auth)
  return jwt && `Bearer ${jwt}`
}
